import { Controller } from "@hotwired/stimulus"

const TRACKED_KEY = "arxiv-funnel-steps"

// Connects to data-controller="arxiv-funnel"
//
// Reports the arXiv page's funnel to GA4 as one event, `arxiv_step`, carrying
// the step in the `step_name` dimension the onboarding wizard already
// registered. One event and one dimension read as a funnel in GA4 and add
// nothing to set up there.
//
// A step named in a value is reported when its element appears; a step named in
// an action parameter is reported when the visitor acts. Bails when gtag is
// absent, as the other controllers here do, so a page without it cannot break.
export default class extends Controller {
  static values = { step: String, subject: String }

  connect() {
    if (this.hasStepValue) this.report(this.stepValue, this.subjectValue)
  }

  track({ params: { step, subject } }) {
    this.report(step, subject)
  }

  // A finished paper is rendered again on every visit to the page, and three
  // finished papers are rendered three times. Without a memory the funnel would
  // read a reload as a conversion, so a step that names its subject is reported
  // once a session for that subject. A step without one — a press the visitor
  // made — counts every time, because they made it every time.
  report(step, subject) {
    if (typeof window.gtag !== "function" || !step) return

    const key = subject ? `${step}:${subject}` : null
    if (key && this.reported.has(key)) return

    window.gtag("event", "arxiv_step", { step_name: step })

    if (!key) return
    const reported = this.reported.add(key)
    this.persist(reported)
  }

  get reported() {
    try {
      const raw = sessionStorage.getItem(TRACKED_KEY)
      const list = raw ? JSON.parse(raw) : []
      return new Set(Array.isArray(list) ? list : [])
    } catch {
      // Storage unavailable or corrupt: no memory, so a step re-fires. Counting
      // twice is better than the page failing.
      return new Set()
    }
  }

  persist(reported) {
    try {
      sessionStorage.setItem(TRACKED_KEY, JSON.stringify([...reported]))
    } catch {
      // Nothing to do: the next render reports again.
    }
  }
}
